import { createCreatorProductStore } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/createStore.ts';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { BaseSection } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/BaseSection.ts';
import { html, View } from 'rune-ts';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { Input_TextEditor } from '@services/MarppleShopStudio/shared/component/atom/Input/Input_TextEditor.ts';
import { Tooltip } from '@services/MarppleShop/renderApp/components/atoms/Tooltip/Tooltip.ts';
import common_klass from './styles/common.module.scss';
import { font, shareFont } from '@services/MarppleShop/shared/typography';
import { Input_ProductContent } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_ProductContent/Input_ProductContent.ts';
import { Input_AddionalRadioGroup } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_AddionalRadioGroup.ts';
import { required_html } from '@services/MarppleShopStudio/shared/component/atom/Input/core/helper.ts';
import { StoreProductQnaType } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/StoreProduct/StoreProduct.ts';
import { Input_TextEditor_Intl } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_TextEditor_Intl.ts';
import { Input_ProductContent_Intl } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_ProductContent/Input_ProductContent_Intl.ts';
import { pipe, head } from '@fxts/core';
import { subscribeStore } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/helper.ts';
import { FormFieldControl } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/type.ts';
import { unEscaper } from '@shared/utils/escape.ts';
import { UtilS } from '@modules/Util/S/Function/module/UtilS.js';

interface DetailInfoSectionData<T extends MODE> {
  store: ReturnType<typeof createCreatorProductStore<T>>;
  is_created_sku?: boolean;
}

export class DetailInfoSection<T extends MODE> extends BaseSection<T, DetailInfoSectionData<T>> {
  private store = this.data.store;
  protected readonly sectionTitle = '상세 정보';

  private inputMap = {
    [formKeyMap.product_story]:
      this.store.mode !== MODE.EDIT_TPL_INTL
        ? new Input_ProductContent({
            name: formKeyMap.product_story,
            value: this.store.formData._.product._.product_story,
            title: '상세 설명',
            store: this.store,
          })
        : new Input_ProductContent_Intl({
            name: formKeyMap.product_story,
            value: this.store.formData._.product._.product_story,
            title: '상세 설명',
            store: this.store,
          }),
    [formKeyMap.product_basic_info]:
      this.store.mode !== MODE.EDIT_TPL_INTL
        ? new Input_TextEditor({
            name: formKeyMap.product_basic_info,
            value: unEscaper(this.store.formData.product_basic_info),
            title: html`
              <div class="${common_klass.title_with_tooltip}">
                <span class="${common_klass.text} ${shareFont('14_bold')}">상품정보제공고시 정보</span>
                ${new Tooltip({
                  id: formKeyMap.product_basic_info,
                  content: html`
                    <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                      <div class="${common_klass.tooltip_content_divider}">
                        <span>·</span>
                        <span
                          >판매자가 상품 정보(원산지, 제조일, A/S 책임자 등)를 사전에 제공토록 하는
                          통신판매업자 의무 규정</span
                        >
                      </div>
                      <div class="${common_klass.tooltip_content_divider}">
                        <span>·</span>
                        <span
                          >상품 상세 설명에 해당 정보가 기재되어 있다면 ‘상품 상세 참고'로 기재해
                          주세요.</span
                        >
                      </div>
                      <div class="${common_klass.tooltip_content_divider}">
                        <span>·</span>
                        <span
                          >품목에 따른 상품 기재 정보를 모르는 경우, 관련 정부 고시 내용을 참고해
                          주세요.</span
                        >
                      </div>
                      <div class="${common_klass.tooltip_content_divider}">
                        <span>&nbsp;</span>
                        <a
                          class="${common_klass.atag}"
                          href="https://www.law.go.kr/%ED%96%89%EC%A0%95%EA%B7%9C%EC%B9%99/%EC%A0%84%EC%9E%90%EC%83%81%EA%B1%B0%EB%9E%98%EB%93%B1%EC%97%90%EC%84%9C%EC%9D%98%EC%83%81%ED%92%88%EB%93%B1%EC%9D%98%EC%A0%95%EB%B3%B4%EC%A0%9C%EA%B3%B5%EC%97%90%EA%B4%80%ED%95%9C%EA%B3%A0%EC%8B%9C"
                          target="_blank"
                          >전자상거래 등에서의 상품 등의 정보 제공에 관한 고시</a
                        >
                      </div>
                    </div>
                  `,
                  size: 'x_small',
                  variant: 'studio',
                })}
              </div>
            `,
            option: {
              placeholder: '상품정보제공고시 정보에 대한 내용을 입력해 주세요.',
              min_rows: 2,
              max_rows: 15,
            },
            onFocusout: (view) => {
              this.store.actions.setField(formKeyMap.product_basic_info, view.getValue());
            },
          })
        : new Input_TextEditor_Intl({
            kr: {
              name: formKeyMap.product_basic_info,
              value: unEscaper(this.store.formData.product_basic_info),
              title: html`
                <div class="${common_klass.title_with_tooltip}">
                  <span class="${common_klass.text} ${shareFont('14_bold')}">상품정보제공고시 정보</span>
                  ${new Tooltip({
                    id: formKeyMap.product_basic_info,
                    content: html`
                      <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                        <div class="${common_klass.tooltip_content_divider}">
                          <span>·</span>
                          <span
                            >판매자가 상품 정보(원산지, 제조일, A/S 책임자 등)를 사전에 제공토록 하는
                            통신판매업자 의무 규정</span
                          >
                        </div>
                        <div class="${common_klass.tooltip_content_divider}">
                          <span>·</span>
                          <span
                            >상품 상세 설명에 해당 정보가 기재되어 있다면 ‘상품 상세 참고'로 기재해
                            주세요.</span
                          >
                        </div>
                        <div class="${common_klass.tooltip_content_divider}">
                          <span>·</span>
                          <span
                            >품목에 따른 상품 기재 정보를 모르는 경우, 관련 정부 고시 내용을 참고해
                            주세요.</span
                          >
                        </div>
                        <div class="${common_klass.tooltip_content_divider}">
                          <span>&nbsp;</span>
                          <a
                            class="${common_klass.atag}"
                            href="https://www.law.go.kr/%ED%96%89%EC%A0%95%EA%B7%9C%EC%B9%99/%EC%A0%84%EC%9E%90%EC%83%81%EA%B1%B0%EB%9E%98%EB%93%B1%EC%97%90%EC%84%9C%EC%9D%98%EC%83%81%ED%92%88%EB%93%B1%EC%9D%98%EC%A0%95%EB%B3%B4%EC%A0%9C%EA%B3%B5%EC%97%90%EA%B4%80%ED%95%9C%EA%B3%A0%EC%8B%9C"
                            target="_blank"
                            >전자상거래 등에서의 상품 등의 정보 제공에 관한 고시</a
                          >
                        </div>
                      </div>
                    `,
                    size: 'x_small',
                    variant: 'studio',
                  })}
                </div>
              `,
              option: {
                placeholder: '상품정보제공고시 정보에 대한 내용을 입력해 주세요.',
                min_rows: 2,
                max_rows: 15,
              },
              onFocusout: (view) => {
                this.store.actions.setField(formKeyMap.product_basic_info, view.getValue());
              },
            },
            en: {
              name: formKeyMap.product_basic_info_en,
              value: unEscaper(this.store.formData.product_basic_info_en),
              title: '',
              option: {
                placeholder: '영문 상품정보제공고시 정보에 대한 내용을 입력해 주세요.',
                min_rows: 2,
                max_rows: 15,
              },
              onFocusout: (view) => {
                this.store.actions.setField(formKeyMap.product_basic_info_en, view.getValue());
              },
            },
            jp: {
              name: formKeyMap.product_basic_info_jp,
              value: unEscaper(this.store.formData.product_basic_info_jp),
              title: '',
              option: {
                placeholder: '일문 상품정보제공고시 정보에 대한 내용을 입력해 주세요.',
                min_rows: 2,
                max_rows: 15,
              },
              onFocusout: (view) => {
                this.store.actions.setField(formKeyMap.product_basic_info_jp, view.getValue());
              },
            },
          }),
    [formKeyMap.is_kc_cert_checked]: new Input_AddionalRadioGroup({
      name: formKeyMap.is_kc_cert_checked,
      additional: {
        views: [
          new Input_TextEditor({
            title: '',
            name: formKeyMap.certification_info,
            value: unEscaper(this.store.formData.certification_info),
            option: {
              placeholder: 'KC 인증번호를 입력해 주세요.',
            },
            onFocusout: (view) => {
              this.store.actions.setField(formKeyMap.certification_info, view.getValue());
            },
          }),
        ],
        isShow: (selected_id) => selected_id === String(true),
      },
      onChange: (show) => {
        this.store.actions.setField(formKeyMap.is_kc_cert_checked, show);
        if (!show) this.store.actions.setField(formKeyMap.certification_info, null);
      },
      selected_id: String(this.store.formData.is_kc_cert_checked || false),
      items: [
        { id: 'true', display_name: 'KC 인증 있음', value: true },
        { id: 'false', display_name: '해당 없음', value: false },
      ],
      title: html`
        <div class="${common_klass.title_with_tooltip}">
          <span class="${common_klass.text} ${shareFont('14_bold')}">KC 및 기타 인증</span>
          ${required_html}
          ${new Tooltip({
            id: formKeyMap.is_kc_cert_checked,
            content: html`
              <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                <div class="${common_klass.tooltip_content_divider}">
                  <span>·</span>
                  <span>KC 인증이 필요한 상품은 KC 인증 및 별도 허가증 등 정보를 기재하여야 합니다.</span>
                </div>
                <div class="${common_klass.tooltip_content_divider}">
                  <span>·</span>
                  <span>하단의 가이드 또는 정부부처의 사이트를 참고해 주세요. </span>
                </div>
                <div class="${common_klass.tooltip_content_divider}">
                  <span>&nbsp;</span>
                  <a
                    class="${common_klass.atag}"
                    href="https://marpple.notion.site/KC-781ae94b5bc344f39a9fec6cbb1c2f9f"
                    target="_blank"
                    >KC 및 기타 인증 안내 가이드</a
                  >
                </div>
              </div>
            `,
            size: 'x_small',
            variant: 'studio',
          })}
        </div>
      `,
    }),
    [formKeyMap.qna_type]: new Input_AddionalRadioGroup({
      name: formKeyMap.qna_type,
      additional: {
        views: [
          new Input_TextEditor({
            title: '',
            name: formKeyMap.qna_link,
            value: this.store.formData.qna_link || '',
            option: {
              placeholder: '예시) https://www.instagram.com/marpple.shop/',
            },
            onFocusout: (view) => {
              this.store.actions.setField(formKeyMap.qna_link, view.getValue() || null);
            },
          }),
        ],
        isShow: (selected_id) => selected_id === StoreProductQnaType.SNS,
      },
      onChange: (show) => {
        this.store.actions.setField(
          formKeyMap.qna_type,
          show ? StoreProductQnaType.SNS : StoreProductQnaType.BOARD,
        );
        if (!show) this.store.actions.setField(formKeyMap.qna_link, null);
      },
      selected_id:
        this.store.formData.qna_type === StoreProductQnaType.SNS
          ? StoreProductQnaType.SNS
          : StoreProductQnaType.BOARD,
      items: [
        {
          id: StoreProductQnaType.BOARD,
          display_name: UtilS.isNessApp() ? '넥슨 에센셜 일대일 문의하기' : '마플샵 일대일 문의하기',
          value: true,
        },
        { id: StoreProductQnaType.SNS, display_name: '개인 SNS로 문의하기', value: false },
      ],
      title: html`
        <div class="${common_klass.title_with_tooltip}">
          <span class="${common_klass.text} ${shareFont('14_bold')}">문의 방법 설정</span>
          ${required_html}
          ${new Tooltip({
            id: formKeyMap.qna_type,
            content: html`
              <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                <div class="${common_klass.tooltip_content_divider}">
                  <div>
                    <b class="${font('14_bold')}">${UtilS.isNessApp() ? '넥슨 에센셜 일대일 문의하기' : '마플샵 일대일 문의하기'}</b>
                    <div class="${common_klass.tooltip_content_divider}">
                      <span>·</span>
                      <span>스튜디오 > 고객 문의 관리를 통해 구매자와 메시지를 주고받을 수 있습니다.</span>
                    </div>
                    <div class="${common_klass.tooltip_content_divider}">
                      <span>·</span>
                      <span>7일 이상 미답변 시 7일 동안 판매가 중지될 수 있습니다.</span>
                    </div>
                  </div>
                </div>
                <div class="${common_klass.tooltip_content_divider}">
                  <div>
                    <b class="${font('14_bold')}">개인 SNS로 문의하기</b>
                    <div class="${common_klass.tooltip_content_divider}">
                      <span>·</span>
                      <span
                        >크리에이터의 SNS를 통해 구매자와 문의, 답변을 주고받습니다. (예 : 크리에이터의
                        인스타그램 등)</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            `,
            size: 'x_small',
            variant: 'studio',
          })}
        </div>
      `,
    }),
  };

  public override inputViews(): Array<View> {
    return Object.values(this.inputMap);
  }

  protected override onRender() {
    const kc_certification_view = pipe(this.inputMap.is_kc_cert_checked.getViews(), head);
    const qna_link_view = pipe(this.inputMap.qna_type.getViews(), head);

    if (kc_certification_view && qna_link_view) {
      subscribeStore(this.store, {
        [formKeyMap.certification_info]: kc_certification_view as unknown as FormFieldControl,
        [formKeyMap.qna_link]: qna_link_view as unknown as FormFieldControl,
      });
    }
  }
}
